import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { type PurposeType, PurposeTypeValues, verifyMfaRisk } from '@/api/modules/auth/generate';
import { type MfaType, MfaTypeValues } from '@/api/modules/auth/login';
import { sendMfaRiskCode } from '@/api/modules/auth/mfa';
import {
  bindEmail,
  bindPhone,
  changeNickname,
  changePassword,
  sendBindEmailCode,
  sendBindPhoneCode,
} from '@/api/modules/user';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Spinner } from '@/components/ui/spinner';
import { useCountdown } from '@/hooks/useCountdown';
import { useUserStore } from '@/stores/userStore';
import { maskPhoneNumber } from '@/utils/helpers';
import { useNavigate } from 'react-router-dom';
import { PasswordInput } from '@/components/ui/password-input';
import { useQueryClient } from '@tanstack/react-query';

interface UserInfoDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

type DialogType =
  | 'info'
  | 'nickname'
  | 'security-check'
  | 'email-bind'
  | 'phone-bind'
  | 'password-change';

// 风险操作上下文
interface RiskVerifyState {
  purpose?: PurposeType;
  token?: string;
  nextStep?: DialogType;
}

// 定义各个表单的类型
interface BaseFormData {
  [key: string]: unknown;
}

interface NicknameFormData extends BaseFormData {
  nickname?: string;
}

interface SecurityVerifyFormData extends BaseFormData {
  mfaType?: MfaType;
  code?: string;
}

interface EmailBindFormData extends BaseFormData {
  newEmail?: string;
  emailCode?: string;
}

interface PhoneBindFormData extends BaseFormData {
  newPhone?: string;
  smsCode?: string;
}

interface PasswordChangeFormData extends BaseFormData {
  newPassword?: string;
  confirmPassword?: string;
}

// 组合所有表单类型
interface AllFormData
  extends
    NicknameFormData,
    SecurityVerifyFormData,
    EmailBindFormData,
    PhoneBindFormData,
    PasswordChangeFormData {}

// 定义对话框配置接口
interface DialogConfig<T = AllFormData> {
  title: string;
  component: React.FC<DialogComponentProps<T>>;
  showFooter?: boolean;
  nextAction?: () => void;
  backAction?: () => void;
  nextButtonText?: string;
}

// 定义组件属性接口
interface DialogComponentProps<T = AllFormData> {
  formData: T;
  setFormData: React.Dispatch<React.SetStateAction<T>>;
  onSwitchForm: (type: DialogType, riskState?: RiskVerifyState) => void;
  loading?: boolean;
  riskVerifyState?: RiskVerifyState;
}

// 账号详情内容组件
const AccountInfoContent: React.FC<DialogComponentProps<AllFormData>> = ({ onSwitchForm }) => {
  const { userInfo } = useUserStore();

  return (
    <div className="space-y-4 py-4 text-sm">
      <div className="flex items-center justify-between w-115">
        <div className="w-95 flex flex-row items-center">
          <Label className="text-muted-foreground w-20 shrink-0">昵称</Label>
          <span className="truncate">
            {userInfo?.nickname || userInfo?.username || userInfo?.email}
          </span>
        </div>
        <Button variant="link" className="h-auto p-0" onClick={() => onSwitchForm('nickname')}>
          更改昵称
        </Button>
      </div>
      <div className="flex items-center justify-between w-115">
        <div className="w-95 flex flex-row items-center">
          <Label className="text-muted-foreground w-20 shrink-0">邮箱</Label>
          <span className="truncate">{userInfo?.email}</span>
        </div>

        <Button
          variant="link"
          className="h-auto p-0"
          onClick={() =>
            onSwitchForm('security-check', {
              purpose: PurposeTypeValues.BindEmail,
              nextStep: 'email-bind',
            })
          }
        >
          更改邮箱
        </Button>
      </div>
      <div className="flex items-center justify-between w-115">
        <div className="w-95 flex flex-row items-center">
          <Label className="text-muted-foreground w-20 shrink-0">密码</Label>
          <span>********</span>
        </div>
        <Button
          variant="link"
          className="h-auto p-0"
          onClick={() =>
            onSwitchForm('security-check', {
              purpose: PurposeTypeValues.ChangePassword,
              nextStep: 'password-change',
            })
          }
        >
          更改密码
        </Button>
      </div>
      <div className="flex items-center justify-between w-115">
        <div className="w-95 flex flex-row items-center">
          <Label className="text-muted-foreground w-20 shrink-0">手机号</Label>
          <span>{maskPhoneNumber(userInfo?.phone || '')}</span>
        </div>
        <Button
          variant="link"
          className="h-auto p-0"
          onClick={() =>
            onSwitchForm('security-check', {
              purpose: PurposeTypeValues.BindPhone,
              nextStep: 'phone-bind',
            })
          }
        >
          更换手机号
        </Button>
      </div>
    </div>
  );
};

// 昵称表单内容组件
const NicknameFormContent: React.FC<DialogComponentProps<NicknameFormData>> = ({
  formData,
  setFormData,
}) => (
  <div className="space-y-4 py-4 text-sm">
    <div className="space-y-2">
      <Label>
        <span className="text-destructive">* </span>昵称
      </Label>
      <Input
        placeholder="请输入昵称"
        value={formData.nickname || ''}
        onChange={(e) => setFormData((prev) => ({ ...prev, nickname: e.target.value }))}
      />
    </div>
  </div>
);

// 安全验证内容组件 (MFA)
const SecurityVerifyContent: React.FC<DialogComponentProps<SecurityVerifyFormData>> = ({
  formData,
  setFormData,
  riskVerifyState,
}) => {
  const { userInfo } = useUserStore();
  const { count, counting, start } = useCountdown();
  const [sending, setSending] = useState(false);

  // 初始化 MFA 类型
  useEffect(() => {
    if (!formData.mfaType) {
      // 优先选择手机验证，如果手机不存在则选择邮箱
      if (userInfo?.phone) {
        setFormData((prev) => ({ ...prev, mfaType: MfaTypeValues.SMS }));
      } else if (userInfo?.email) {
        setFormData((prev) => ({ ...prev, mfaType: MfaTypeValues.Email }));
      }
    }
  }, [userInfo, formData.mfaType, setFormData]);

  const handleSendCode = async () => {
    if (!formData.mfaType || !riskVerifyState?.purpose) return;

    try {
      setSending(true);
      const res = await sendMfaRiskCode(formData.mfaType, riskVerifyState.purpose);
      if (res.code === 0) {
        toast.success('验证码发送成功');
        start();
      }
    } catch (error) {
      console.error('发送验证码失败:', error);
    } finally {
      setSending(false);
    }
  };

  const hasPhone = !!userInfo?.phone;
  const hasEmail = !!userInfo?.email;

  // 如果没有绑定任何验证方式，显示提示
  if (!hasPhone && !hasEmail) {
    return (
      <div className="py-8 text-center text-muted-foreground">
        未绑定手机或邮箱，无法进行安全验证，请联系管理员。
      </div>
    );
  }

  return (
    <div className="space-y-4 py-4 text-sm">
      <div className="space-y-2">
        <Label>验证方式</Label>
        <RadioGroup
          value={formData.mfaType}
          onValueChange={(val) => setFormData((prev) => ({ ...prev, mfaType: val as MfaType }))}
          className="flex flex-col gap-2"
        >
          {hasPhone && (
            <div className="flex items-center space-x-2">
              <RadioGroupItem value={MfaTypeValues.SMS} id="sms" />
              <Label htmlFor="sms">手机验证 ({maskPhoneNumber(userInfo.phone)})</Label>
            </div>
          )}
          {hasEmail && (
            <div className="flex items-center space-x-2">
              <RadioGroupItem value={MfaTypeValues.Email} id="email" />
              <Label htmlFor="email">邮箱验证 ({userInfo.email})</Label>
            </div>
          )}
        </RadioGroup>
      </div>
      <div className="space-y-2">
        <Label>
          <span className="text-destructive">* </span>验证码
        </Label>
        <div className="flex gap-2">
          <Input
            placeholder="请输入6位验证码"
            className="flex-1"
            maxLength={6}
            value={formData.code || ''}
            onChange={(e) => setFormData((prev) => ({ ...prev, code: e.target.value }))}
          />
          <Button
            className="shrink-0 w-35"
            disabled={counting || sending || !formData.mfaType}
            onClick={handleSendCode}
          >
            {counting ? `获取验证码(${count}s)` : sending ? <Spinner /> : '获取验证码'}
          </Button>
        </div>
      </div>
    </div>
  );
};

// 绑定邮箱内容组件
const EmailBindContent: React.FC<DialogComponentProps<EmailBindFormData>> = ({
  formData,
  setFormData,
  riskVerifyState,
}) => {
  const { count, counting, start } = useCountdown();
  const [sending, setSending] = useState(false);

  const handleSendCode = async () => {
    if (!formData.newEmail || !riskVerifyState?.token) {
      toast.error('请输入邮箱');
      return;
    }

    try {
      setSending(true);
      const res = await sendBindEmailCode(riskVerifyState.token, formData.newEmail);
      if (res.code === 0) {
        toast.success('验证码发送成功');
        start();
      }
    } catch (error) {
      console.error('发送验证码失败:', error);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="space-y-4 py-4 text-sm">
      <div className="space-y-2">
        <Label>
          <span className="text-destructive">* </span>新邮箱
        </Label>
        <Input
          placeholder="请输入新邮箱"
          value={formData.newEmail || ''}
          onChange={(e) => setFormData((prev) => ({ ...prev, newEmail: e.target.value }))}
        />
      </div>
      <div className="space-y-2">
        <Label>
          <span className="text-destructive">* </span>验证码
        </Label>
        <div className="flex gap-2">
          <Input
            placeholder="请输入6位验证码"
            className="flex-1"
            maxLength={6}
            value={formData.emailCode || ''}
            onChange={(e) => setFormData((prev) => ({ ...prev, emailCode: e.target.value }))}
          />
          <Button
            className="shrink-0 w-35"
            disabled={counting || sending || !formData.newEmail}
            onClick={handleSendCode}
          >
            {counting ? `获取验证码(${count}s)` : sending ? <Spinner /> : '获取验证码'}
          </Button>
        </div>
      </div>
    </div>
  );
};

// 绑定手机内容组件
const PhoneBindContent: React.FC<DialogComponentProps<PhoneBindFormData>> = ({
  formData,
  setFormData,
  riskVerifyState,
}) => {
  const { count, counting, start } = useCountdown();
  const [sending, setSending] = useState(false);

  const handleSendCode = async () => {
    if (!formData.newPhone || !riskVerifyState?.token) {
      toast.error('请输入手机号');
      return;
    }

    try {
      setSending(true);
      const res = await sendBindPhoneCode(riskVerifyState.token, formData.newPhone);
      if (res.code === 0) {
        toast.success('验证码发送成功');
        start();
      }
    } catch (error) {
      console.error('发送验证码失败:', error);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="space-y-4 py-4 text-sm">
      <div className="space-y-2">
        <Label>
          <span className="text-destructive">* </span>新手机号
        </Label>
        <Input
          placeholder="请输入新手机号"
          value={formData.newPhone || ''}
          onChange={(e) => setFormData((prev) => ({ ...prev, newPhone: e.target.value }))}
        />
      </div>
      <div className="space-y-2">
        <Label>
          <span className="text-destructive">* </span>验证码
        </Label>
        <div className="flex gap-2">
          <Input
            placeholder="请输入6位验证码"
            className="flex-1"
            maxLength={6}
            value={formData.smsCode || ''}
            onChange={(e) => setFormData((prev) => ({ ...prev, smsCode: e.target.value }))}
          />
          <Button
            className="shrink-0 w-35"
            disabled={counting || sending || !formData.newPhone}
            onClick={handleSendCode}
          >
            {counting ? `获取验证码(${count}s)` : sending ? <Spinner /> : '获取验证码'}
          </Button>
        </div>
      </div>
    </div>
  );
};

// 修改密码内容组件
const PasswordChangeContent: React.FC<DialogComponentProps<PasswordChangeFormData>> = ({
  formData,
  setFormData,
}) => (
  <div className="space-y-4 py-4 text-sm">
    <div className="space-y-2">
      <Label>
        <span className="text-destructive">* </span>新密码
      </Label>
      <PasswordInput
        placeholder="请输入新密码"
        value={formData.newPassword || ''}
        onChange={(e) => setFormData((prev) => ({ ...prev, newPassword: e.target.value }))}
      />
    </div>
    <div className="space-y-2">
      <Label>
        <span className="text-destructive">* </span>确认新密码
      </Label>
      <PasswordInput
        placeholder="请再次输入新密码"
        value={formData.confirmPassword || ''}
        onChange={(e) => setFormData((prev) => ({ ...prev, confirmPassword: e.target.value }))}
      />
    </div>
  </div>
);

export function UserInfoDialog({ open, onOpenChange }: UserInfoDialogProps) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [currentDialog, setCurrentDialog] = useState<DialogType>('info');
  const [formData, setFormData] = useState<AllFormData>({});
  const [loading, setLoading] = useState(false);
  const [riskVerifyState, setRiskVerifyState] = useState<RiskVerifyState>({});
  const { userInfo, setUserInfo, clearUser } = useUserStore();

  // 处理表单切换
  const handleSwitchForm = useCallback((type: DialogType, riskState?: RiskVerifyState) => {
    setCurrentDialog(type);
    if (riskState) {
      setRiskVerifyState((prev) => ({ ...prev, ...riskState }));
    }
  }, []);

  // 清空表单数据
  const clearFormData = useCallback(() => {
    setFormData({});
    setRiskVerifyState({});
  }, []);

  // 重置对话框状态
  const resetDialog = useCallback(() => {
    setCurrentDialog('info');
    clearFormData();
  }, [clearFormData]);

  // 处理保存昵称
  const handleSaveNickname = useCallback(async () => {
    const nickname = formData.nickname?.trim();
    if (!nickname) {
      toast.error('请输入昵称');
      return;
    }

    try {
      setLoading(true);
      const res = await changeNickname(nickname);

      if (res.code === 0) {
        if (userInfo) {
          setUserInfo({ ...userInfo, nickname });
        }

        toast.success('昵称修改成功');
        handleSwitchForm('info');
        queryClient.removeQueries();
      }
    } catch (error) {
      console.error('修改昵称失败:', error);
    } finally {
      setLoading(false);
    }
  }, [formData.nickname, userInfo, handleSwitchForm, queryClient, setUserInfo]);

  // 处理安全验证 (MFA)
  const handleSecurityVerify = useCallback(async () => {
    if (!formData.mfaType || !formData.code || !riskVerifyState.purpose) {
      toast.error('请完善验证信息');
      return;
    }

    try {
      setLoading(true);
      const res = await verifyMfaRisk(formData.mfaType, riskVerifyState.purpose, formData.code);

      if (res.code === 0) {
        toast.success('验证通过');

        // 更新 token 并进入下一步
        if (res.data?.tempToken && riskVerifyState.nextStep) {
          handleSwitchForm(riskVerifyState.nextStep, { token: res.data.tempToken });
          // 清空表单数据以便下一步使用（如果不希望清空所有，可以只清空验证码等）
          setFormData({});
        }
      }
    } catch (error) {
      console.error('验证失败:', error);
    } finally {
      setLoading(false);
    }
  }, [formData.mfaType, formData.code, riskVerifyState, handleSwitchForm]);

  // 处理绑定邮箱
  const handleBindEmail = useCallback(async () => {
    if (!formData.newEmail || !formData.emailCode || !riskVerifyState.token) {
      toast.error('请完善邮箱信息');
      return;
    }

    try {
      setLoading(true);
      const res = await bindEmail({
        email: formData.newEmail,
        emailCode: formData.emailCode,
        token: riskVerifyState.token,
      });

      if (res.code === 0) {
        if (userInfo) {
          setUserInfo({ ...userInfo, email: formData.newEmail });
        }

        toast.success('邮箱绑定成功,请重新登录');
        clearUser();
        navigate('/login', { replace: true });
      }
    } catch (error) {
      console.error('绑定邮箱失败:', error);
    } finally {
      setLoading(false);
    }
  }, [
    formData.newEmail,
    formData.emailCode,
    riskVerifyState.token,
    userInfo,
    navigate,
    setUserInfo,
    clearUser,
  ]);

  // 处理绑定手机
  const handleBindPhone = useCallback(async () => {
    if (!formData.newPhone || !formData.smsCode || !riskVerifyState.token) {
      toast.error('请完善手机信息');
      return;
    }

    try {
      setLoading(true);
      const res = await bindPhone({
        phone: formData.newPhone,
        smsCode: formData.smsCode,
        token: riskVerifyState.token,
      });

      if (res.code === 0) {
        if (userInfo) {
          setUserInfo({ ...userInfo, phone: formData.newPhone });
        }

        toast.success('手机绑定成功,请重新登录');
        clearUser();
        navigate('/login', { replace: true });
      }
    } catch (error) {
      console.error('绑定手机失败:', error);
    } finally {
      setLoading(false);
    }
  }, [
    formData.newPhone,
    formData.smsCode,
    riskVerifyState.token,
    userInfo,
    navigate,
    setUserInfo,
    clearUser,
  ]);

  // 处理修改密码
  const handleChangePassword = useCallback(async () => {
    if (!formData.newPassword || !formData.confirmPassword || !riskVerifyState.token) {
      toast.error('请完善密码信息');
      return;
    }

    if (formData.newPassword !== formData.confirmPassword) {
      toast.error('两次输入的密码不一致');
      return;
    }

    try {
      setLoading(true);
      const res = await changePassword({
        newPassword: formData.newPassword,
        confirmPassword: formData.confirmPassword,
        token: riskVerifyState.token,
      });

      if (res.code === 0) {
        toast.success('密码修改成功');
        clearUser();
        navigate('/login', { replace: true });
      }
    } catch (error) {
      console.error('修改密码失败:', error);
    } finally {
      setLoading(false);
    }
  }, [formData.newPassword, formData.confirmPassword, riskVerifyState.token, clearUser, navigate]);

  // 对话框配置映射
  const dialogConfigs: Record<DialogType, DialogConfig> = useMemo(
    () => ({
      info: {
        title: '账号详情',
        component: AccountInfoContent,
        showFooter: false,
      },
      nickname: {
        title: '更改昵称',
        component: NicknameFormContent,
        showFooter: true,
        backAction: () => handleSwitchForm('info'),
        nextAction: handleSaveNickname,
        nextButtonText: '保存',
      },
      'security-check': {
        title: '安全验证',
        component: SecurityVerifyContent,
        showFooter: true,
        backAction: () => handleSwitchForm('info'),
        nextAction: handleSecurityVerify,
        nextButtonText: '下一步',
      },
      'email-bind': {
        title: '绑定新邮箱',
        component: EmailBindContent,
        showFooter: true,
        backAction: () => handleSwitchForm('info'),
        nextAction: handleBindEmail,
        nextButtonText: '确认绑定',
      },
      'phone-bind': {
        title: '绑定新手机',
        component: PhoneBindContent,
        showFooter: true,
        backAction: () => handleSwitchForm('info'),
        nextAction: handleBindPhone,
        nextButtonText: '确认绑定',
      },
      'password-change': {
        title: '修改密码',
        component: PasswordChangeContent,
        showFooter: true,
        backAction: () => handleSwitchForm('info'),
        nextAction: handleChangePassword,
        nextButtonText: '确认修改',
      },
    }),
    [
      handleSwitchForm,
      handleSaveNickname,
      handleSecurityVerify,
      handleBindEmail,
      handleBindPhone,
      handleChangePassword,
    ]
  );

  // 渲染对话框内容
  const renderDialogContent = useMemo(() => {
    const config = dialogConfigs[currentDialog];
    const Component = config.component;
    const isInfo = currentDialog === 'info';

    return (
      <DialogContent
        className="w-auto max-w-none!"
        onInteractOutside={(e) => {
          if (!isInfo) {
            e.preventDefault();
          }
        }}
      >
        <DialogHeader>
          <DialogTitle>{config.title}</DialogTitle>
        </DialogHeader>

        <Component
          formData={formData}
          setFormData={setFormData}
          onSwitchForm={handleSwitchForm}
          loading={loading}
          riskVerifyState={riskVerifyState}
        />

        {config.showFooter && (
          <DialogFooter>
            {config.backAction && (
              <Button variant="outline" onClick={config.backAction} disabled={loading}>
                取消
              </Button>
            )}
            <Button onClick={config.nextAction || (() => {})} disabled={loading}>
              {loading && <Spinner className="mr-2 h-4 w-4" />}
              {config.nextButtonText || '保存'}
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    );
  }, [currentDialog, dialogConfigs, formData, handleSwitchForm, loading, riskVerifyState]);

  return (
    <Dialog
      open={open}
      onOpenChange={(isOpen) => {
        onOpenChange(isOpen);
        if (!isOpen) {
          // 关闭时重置为账号详情页
          setTimeout(resetDialog, 200);
        }
      }}
    >
      {renderDialogContent}
    </Dialog>
  );
}
